import { Button, Label, Textarea } from "@fluentui/react-components";
import React, { useState, useCallback, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router";
import { useAppAccount } from "../../../../AppProvider/AppAccountProvider";

import { postAdminEditFrontendAddonHeader } from "@/api/admin/frontendAddons";
import { postEditFrontendAddonHeader } from "@/api/frontendAddons";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { InfoTooltipLabel } from "@/components/InfoTooltipLabel";
import {
  FrontendAddonRawForm,
  FrontendAddonWithTargetVersionRaw,
  initialFrontendAddonRawForm,
} from "@/domain/FrontendAddon";
import { useAppRouteParams } from "../../../../../AppRoutes";
import { DialogPhaseType, useDialog } from "../../../../Dialog/helpers/hooks";
import { LayoutTemplate } from "../../../../LayoutTemplate";
import { WarningMessageForProduction } from "../../../helpers/WarningMessageForProduction";
import cardTitleStyles from "../../../helpers/cardTitle.module.css";
import styles from "./index.module.css";

const toFrontendAddonForm = (
  targetFrontendAddon: FrontendAddonWithTargetVersionRaw | undefined
): FrontendAddonRawForm =>
  targetFrontendAddon
    ? {
        description: targetFrontendAddon.description ?? "",
        privacy_setting: targetFrontendAddon.privacy_setting,
        owner_org_id: targetFrontendAddon.owner_org_id ?? "",
      }
    : { ...initialFrontendAddonRawForm };

export const FrontendAddonPropertyUpdate = <
  T extends FrontendAddonWithTargetVersionRaw,
>({
  isProd,
  returnPath,
  runReloadFrontendAddons,
  targetFrontendAddon,
  renderExtraFields,
  belowCardElement,
  onSave,
}: {
  isProd: boolean;
  returnPath: string;
  runReloadFrontendAddons: () => Promise<void>;
  targetFrontendAddon: T | undefined;
  renderExtraFields?: (props: { isDisableForm: boolean }) => {
    node: ReactNode;
    isDisabledSave?: boolean;
  };
  // 設定カードの下に並べる要素 (利用組織の注記カードなど)
  belowCardElement?: ReactNode;
  onSave?: () => Promise<void>;
}) => {
  const navigate = useNavigate();

  const { organizationId, frontendAddonId } = useAppRouteParams();
  const { isAADeveloperOrAbove } = useAppAccount();
  const { hasPermission } = usePermissions();
  const hasWritePermission =
    isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "write");
  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();

  const [updatedFrontendAddon, setUpdatedFrontendAddon] = useState(() =>
    toFrontendAddonForm(targetFrontendAddon)
  );

  useEffect(() => {
    if (!targetFrontendAddon) return;

    setUpdatedFrontendAddon(toFrontendAddonForm(targetFrontendAddon));
  }, [organizationId, targetFrontendAddon]);

  const [dialogPhase, setDialogPhase] = useState<DialogPhaseType>(null);
  const { renderDialogs, isInProgress } = useDialog({
    dialogPhase,
    setDialogPhase,
  });

  const runUpdateFrontendAddon = useCallback(async () => {
    setDialogPhase("in_progress");
    try {
      isAADeveloperOrAbove
        ? await postAdminEditFrontendAddonHeader({
            FrontendAddonHeaderRawForm: updatedFrontendAddon,
            frontendAddonId,
          })
        : await postEditFrontendAddonHeader({
            organizationId,
            FrontendAddonHeaderRawForm: updatedFrontendAddon,
            frontendAddonId,
          });
      await onSave?.();
      await runReloadFrontendAddons();
      showSuccessNotification("フロントエンドアドオンを更新しました");
      navigate(returnPath);
    } catch (error) {
      showErrorNotification(
        "フロントエンドアドオンの更新に失敗しました",
        error
      );
    }
    setDialogPhase(null);
  }, [updatedFrontendAddon, organizationId, onSave]);

  const isDisableForm =
    !hasWritePermission ||
    isInProgress ||
    !targetFrontendAddon ||
    targetFrontendAddon.is_disabled ||
    !targetFrontendAddon.is_editable;

  const renderedExtraFields = renderExtraFields?.({ isDisableForm }) ?? null;

  const renderFormBody = () => (
    <div className={styles.formField}>
      <div className={styles.inputContainer}>
        <InfoTooltipLabel info='ZIPファイル内 index.json の "displayName" から設定されます'>
          フロントエンドアドオン名
        </InfoTooltipLabel>
        <div className={styles.readOnlyValue}>{targetFrontendAddon?.name}</div>
      </div>
      <div className={styles.inputContainer}>
        <InfoTooltipLabel info='ZIPファイル内 index.json の "id" から設定されます。IDは初回登録後に変更できません。他のアドオンとIDが重複するとエラーになります。'>
          フロントエンドアドオンID
        </InfoTooltipLabel>
        <div className={styles.readOnlyValue}>{targetFrontendAddon?.id}</div>
      </div>
      <div
        className={`${styles.inputContainer} ${styles.inputContainerEditable}`}
      >
        <Label>概要</Label>
        <Textarea
          className={styles.descriptionTextarea}
          resize="vertical"
          value={updatedFrontendAddon.description ?? ""}
          onChange={(_, data) => {
            setUpdatedFrontendAddon((state) => ({
              ...state,
              description: data.value,
            }));
          }}
          disabled={isDisableForm}
        />
      </div>
      {renderedExtraFields ? (
        <div className={styles.inputContainer}>{renderedExtraFields.node}</div>
      ) : null}
    </div>
  );

  // extraFields は変更検知を持たないため、存在する場合は常に保存可
  const isDescriptionChanged =
    (updatedFrontendAddon.description ?? "") !==
    (targetFrontendAddon?.description ?? "");

  const isDisableSave =
    !hasWritePermission ||
    isInProgress ||
    isDisableForm ||
    renderedExtraFields?.isDisabledSave ||
    (!renderedExtraFields && !isDescriptionChanged);

  // 概要のみの変更は確認ダイアログを挟まない (本番環境と概要以外の更新は挟む)
  const needsConfirmDialog =
    isProd || Boolean(renderedExtraFields) || Boolean(onSave);

  const renderRightHeader = () => {
    return (
      <div className={styles.buttonContainer}>
        <Button
          appearance="primary"
          disabled={isDisableSave}
          onClick={() => {
            if (needsConfirmDialog) {
              setDialogPhase("confirm");
              return;
            }
            runUpdateFrontendAddon();
          }}
        >
          保存
        </Button>
        <Button
          appearance="secondary"
          onClick={() => {
            navigate(returnPath);
          }}
          disabled={isInProgress}
        >
          キャンセル
        </Button>
      </div>
    );
  };

  return (
    <>
      <div className={styles.cardsContainer}>
        <LayoutTemplate
          bodyElement={renderFormBody()}
          leftHeaderElement={
            <>
              “
              <span
                className={`${cardTitleStyles.cardTitle} ${styles.cardTitle}`}
                title={targetFrontendAddon?.name ?? ""}
              >
                {targetFrontendAddon?.name ?? ""}
              </span>
              ”の設定
            </>
          }
          rightHeaderElement={renderRightHeader()}
        />
        {belowCardElement}
      </div>

      {renderDialogs({
        confirm: {
          element: isProd ? (
            <WarningMessageForProduction isEdit={true} />
          ) : (
            <>フロントエンドアドオンを更新しますか？</>
          ),
          title: "フロントエンドアドオン更新",
          buttonText: "更新",
          onClick: runUpdateFrontendAddon,
        },
        inProgress: {
          title: "フロントエンドアドオンの更新中",
        },
      })}
    </>
  );
};
